/**
 * @fileoverview What a student is asked to practice, and how much of it they
 * have logged (C1, C6): the rules Home's "Practice this week" and the Log
 * practice dialog share. The web twin of mobile/src/lib/studentPractice.js;
 * keep the two the same.
 */

/** The lengths Log practice offers, in minutes; "Other" takes any 1 to 720. */
export const DURATIONS = Object.freeze([10, 15, 20, 30, 45, 60]);

/** The longest session one log takes (the server's `duration_minutes` bound). */
export const MAX_MINUTES = 720;

/** A lesson's day: when it was meant to happen, else when it was written. */
export function lessonDay(lesson) {
  const day = lesson?.scheduled_date || lesson?.created_at || "";
  return String(day).slice(0, 10);
}

/** The practice items of a lesson, trimmed, blanks and repeats dropped. */
export function practiceItems(lesson) {
  const seen = new Set();
  const out = [];
  for (const raw of lesson?.practice_items || []) {
    const item = String(raw || "").trim();
    if (item && !seen.has(item)) {
      seen.add(item);
      out.push(item);
    }
  }
  return out;
}

/**
 * The latest lesson that set something to practice: what "Practice this week"
 * lists. The order is read from the lessons' days, so it never depends on the
 * order they came back in.
 *
 * @param {Array<Object>} lessons
 * @returns {Object|null}
 */
export function latestPracticeLesson(lessons) {
  const withItems = (lessons || []).filter((l) => practiceItems(l).length > 0);
  if (!withItems.length) return null;
  return withItems.reduce((latest, lesson) => (lessonDay(lesson) > lessonDay(latest) ? lesson : latest));
}

/**
 * Minutes logged for one practice item since a day (the lesson that set it).
 *
 * @param {Array<Object>} logs - The student's practice logs.
 * @param {string} item - The item's words, as the log carries them (`practice_item`).
 * @param {string} [since] - YYYY-MM-DD; logs before it are for an earlier lesson.
 * @returns {number}
 */
export function minutesFor(logs, item, since = "") {
  return (logs || [])
    .filter((log) => log?.practice_item === item && String(log?.date || "") >= since)
    .reduce((sum, log) => sum + (Number(log.duration_minutes) || 0), 0);
}

/** "5-day streak · 145 min this week", or null when there is nothing to say yet. */
export function streakLine(stats) {
  if (!stats) return null;
  const streak = Number(stats.current_streak_days) || 0;
  const week = Number(stats.week_minutes) || 0;
  const parts = [];
  if (streak > 0) parts.push(`${streak}-day streak`);
  if (week > 0) parts.push(`${week} min this week`);
  return parts.length ? parts.join(" · ") : null;
}

/** Today, or a day either side, as YYYY-MM-DD on the student's own calendar. */
export function localDay(offset = 0, now = new Date()) {
  const d = new Date(now);
  d.setDate(d.getDate() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
